import React, { CSSProperties, ReactElement, useState } from 'react';
import {
  chakra,
  InputProps as ChakraInputProps,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  FormLabel,
  FormControlProps,
  FormLabelProps,
  HelpTextProps,
  FormErrorMessageProps,
} from '@chakra-ui/react';
import clx from 'classnames';
import { HideIcon, ShowIcon } from './icons';

export interface StemiInputProps extends ChakraInputProps {
  label?: string;
  value?: string;
  defaultValue?: string;
  type?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  errors?: string[];
  errEl?: ReactElement;
  showPasswordToggle?: boolean;
  required?: boolean;
  growOnError?: boolean;

  className?: string;
  wrapperStyle?: CSSProperties;
  noClass?: boolean;
  helpText?: string;
  formControlVariant?: FormControlProps['variant'];
  link?: ReactElement;
  labelProps?: FormLabelProps;
  helpProps?: HelpTextProps;
  errorProps?: FormErrorMessageProps;
  rightElement?: ReactElement;
}

const _input: React.FC<StemiInputProps> = (props) => {
  let {
    label,
    type = 'text',
    value,
    defaultValue,
    error,
    errors,
    errEl,
    showPasswordToggle,
    className,
    required,
    growOnError = true,
    noClass,
    wrapperStyle,
    helpText,
    formControlVariant = 'floating',
    link,
    labelProps,
    helpProps,
    errorProps,
    rightElement,
    ...rest
  } = props;
  const [showPass, setShowPass] = useState(false);

  if (error) {
    if (errors) errors?.push(error);
    else errors = [error];
  }

  return (
    <FormControl
      variant={formControlVariant}
      isRequired={required}
      isInvalid={error != null || (errors && errors.length > 0)}
      style={wrapperStyle}
      pb={growOnError && errors ? 4 : 0}
    >
      <InputGroup>
        <Input
          value={value}
          defaultValue={defaultValue}
          placeholder={label}
          size={props.size || 'lg'}
          {...rest}
          className={clx([!noClass && 'form-group', className])}
          type={showPass ? 'text' : type}
        />
        {label && <FormLabel {...labelProps}>{label}</FormLabel>}

        {showPasswordToggle && type === 'password' ? (
          <InputRightElement width="4.5rem" h="full" mr={0}>
            <IconButton
              variant="ghost"
              colorScheme="blue"
              icon={
                showPass ? <HideIcon w={8} h={8} /> : <ShowIcon w={8} h={8} />
              }
              onClick={() => setShowPass((s) => !s)}
              aria-label="Show password"
            />
          </InputRightElement>
        ) : null}
        {rightElement && (
          <InputRightElement h="full" pr={4}>
            {rightElement}
          </InputRightElement>
        )}
        {link && (
          <chakra.div
            position="absolute"
            top="-20px"
            right="0"
            fontSize="11px"
            fontWeight="bold"
            color="slategray"
          >
            {link}
          </chakra.div>
        )}
      </InputGroup>
      {!errors ? (
        helpText ? (
          <FormHelperText {...helpProps}>{helpText}</FormHelperText>
        ) : null
      ) : (
        <>
          {errors.map((error, index) => (
            <FormErrorMessage key={index} {...errorProps}>
              {error}
              {errEl}
            </FormErrorMessage>
          ))}
        </>
      )}
    </FormControl>
  );
};

export const StemiInput: React.FC<StemiInputProps> = chakra(_input);
